import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate, useRouteLoaderData, useSearchParams } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import type { RootLoaderData } from '~/root';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { useCommunityDisplayName } from '../../_hooks/useCommunityDisplayName';
import { useCommunityPurchasableTiers } from '../../_hooks/useCommunityPurchasableTiers';
import { useCommunityUpgradeReturn } from '../../_hooks/useCommunityUpgradeReturn';
import { BaseUpgradeModal } from '../BaseUpgradeModal';
import { pollAccessRoute } from '../BaseUpgradeModal/useAccessPoll';
import type { UpgradeJoinHandoff } from './useUpgradeJoinHandoff';

const checkCommunityAccess = pollAccessRoute('/community_access', 'community_accessible');

interface CommunityUpgradeModalProps {
  handoff: UpgradeJoinHandoff;
}

/**
 * Sells community-wide access, then hands the buyer to the join form.
 *
 * Mounted only while open (and after auth resolves), so the shell derives its
 * initial step once at mount. The post-purchase handoff lives in the layout
 * (useUpgradeJoinHandoff): it keeps running after this component unmounts on close.
 */
export const CommunityUpgradeModal: FC<CommunityUpgradeModalProps> = ({
  handoff: { destination, isAwaitingAccess, markAwaitingAccess, onAccessGranted },
}) => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const isLoggedIn = Boolean(rootData?.isLoggedIn);
  const { handleOpenModal } = useGlobalModals();
  const pricing = useCommunityPurchasableTiers();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const communityName = useCommunityDisplayName();
  const { successRedirectTo, signInRedirectTo } = useCommunityUpgradeReturn(destination);

  const upgradeReturn = searchParams.get('upgrade_return') === '1';

  const handleGuestSuccess = (ssoLoginUrl?: string) => {
    if (ssoLoginUrl) {
      // Guest purchase — hop to OTP login, returning via successRedirectTo; client-side so the payment toast survives.
      navigate(ssoLoginUrl);
      return;
    }

    // Guest without an SSO hop (Swarm only mints one for an email it already knew): verify the email first, then return.
    handleOpenModal(
      'community_login',
      { preventScrollReset: true },
      { redirect_to: successRedirectTo, upgrade_return: '1' },
    );
  };

  const footer = !isLoggedIn ? (
    <p className="mt-8 text-center text-[13px] leading-6 text-black/50">
      {t('community.join.already_member')}{' '}
      <button
        type="button"
        onClick={() =>
          handleOpenModal('community_login', { preventScrollReset: true }, { redirect_to: signInRedirectTo })
        }
        className="underline transition-colors duration-150 hover:text-black"
      >
        {t('shared.cta.sign_in')}
      </button>
    </p>
  ) : undefined;

  return (
    <BaseUpgradeModal
      isOpen
      title={t('community.upgrade_modal.title', { communityName })}
      impressionPageBlock={NectarPageBlock.CommunityUpgradeModal}
      pricing={pricing}
      checkAccess={checkCommunityAccess}
      isAwaitingAccess={isAwaitingAccess}
      onPaymentAccepted={markAwaitingAccess}
      onAccessGranted={onAccessGranted}
      successRedirectTo={successRedirectTo}
      onGuestSuccess={handleGuestSuccess}
      footer={footer}
      resumeAwaitingAccessOnOpen={upgradeReturn}
    />
  );
};
